// Shared name comparison for CP KYC verification.
//
// During signup and profile edits we compare the same firm or person name as it
// appears in three different records — the bank account holder, the GST
// business/legal name, and the RERA agent name — and none of them agree on
// spacing, punctuation, word order or abbreviation:
//
//   Name as per bank : Homelocale Reality
//   Name as per GST  : Home Locale Reality
//   Name as per RERA : Homelocale Reality
//
// This replaces four duplicated matchers (Register.jsx had normalizeName +
// wordsMatch + partialWordsMatch; Profile.jsx had its own wordsMatch,
// isPartialMatch and a *different* partialWordsMatch) that disagreed with each
// other, so the same pair could pass on one screen and fail on another.
//
// The rule that matters: two names match when their DISTINCTIVE part matches.
// The old logic accepted any single shared word of four or more characters,
// which in real estate means the generic suffix carried the match — "Ram
// Enterprises" passed against "Shyam Enterprises", and "Amol Salve" against
// "Rahul Salve". Those are now rejected.

// Words that carry no identity in this domain. Two names sharing only these are
// not the same business. Derived from token frequency across the live CP table,
// so it reflects the names actually in use rather than a guess.
const GENERIC_WORDS = new Set([
  // legal form
  "PRIVATE", "LIMITED", "LTD", "PVT", "LLP", "LLC", "INC", "CO", "COMPANY",
  "AND", "THE", "OF", "M", "MS", "MR", "MRS", "SHRI", "SMT",
  // trade descriptors
  "REAL", "REALTY", "REALITY", "REALTORS", "REALTOR", "REALTECH",
  "PROPERTY", "PROPERTIES", "PROP", "ESTATE", "ESTATES",
  "DEVELOPER", "DEVELOPERS", "DEVELOPMENT", "DEVELOPMENTS",
  "BUILDER", "BUILDERS", "CONSTRUCTION", "CONSTRUCTIONS",
  "ENTERPRISE", "ENTERPRISES", "ASSOCIATES", "ASSOCIATE",
  "CONSULTANT", "CONSULTANTS", "CONSULTANCY", "CONSULTING",
  "INFRA", "INFRATECH", "INFRASTRUCTURE", "VENTURE", "VENTURES",
  "SOLUTION", "SOLUTIONS", "SERVICE", "SERVICES", "GROUP",
  "MARKETING", "HOUSING", "HOMES", "HOME", "SPACES", "SPACE",
  "PROPRIETOR", "PROPRIETORSHIP", "FIRM",
]);

// Canonical form for display and word-level comparison: expand the common
// "Pvt Ltd" abbreviation, drop dots and commas, collapse whitespace, uppercase.
// Kept byte-compatible with the previous local copies, because callers store
// the result as billingName.
export function normalizeName(name) {
  return name
    ?.replace(/PVT\.?\s*LTD\.?/gi, "PRIVATE LIMITED")
    ?.replace(/[.,]/g, "")
    ?.replace(/\s+/g, " ")
    ?.trim()
    ?.toUpperCase();
}

// Everything that is not a letter or digit removed. This is what makes
// "Homelocale Reality" and "Home Locale Reality" comparable — both collapse to
// HOMELOCALEREALITY. It also absorbs "M/s", hyphens, "&" and stray punctuation.
function squash(name) {
  return String(name || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function tokens(name) {
  return String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

// Same name once word boundaries and punctuation are ignored, or one wholly
// contained in the other ("HOMELOCALE REALITY" vs "HOMELOCALE REALITY LLP").
// Containment needs 6+ characters so a short token cannot swallow a longer,
// unrelated name.
function squashedEqual(a, b) {
  const x = squash(a);
  const y = squash(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const shorter = x.length <= y.length ? x : y;
  const longer = x.length <= y.length ? y : x;
  return shorter.length >= 6 && longer.includes(shorter);
}

// Order-insensitive: the same words rearranged are the same name.
// "RUPESH KAILAS MALI" === "KAILAS MALI RUPESH".
function sameTokenSet(a, b) {
  const x = [...new Set(tokens(a))].sort();
  const y = [...new Set(tokens(b))].sort();
  if (!x.length || x.length !== y.length) return false;
  // Pair them up allowing spelling variants, so a reordered name with a
  // transliteration difference still matches.
  const spare = [...y];
  return x.every((t) => {
    const at = spare.findIndex((o) => tokenEquivalent(t, o));
    if (at === -1) return false;
    spare.splice(at, 1);
    return true;
  });
}

// Levenshtein distance, capped: stops as soon as it exceeds `max`, so this
// never walks the full matrix for words that are obviously different.
function withinEditDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
      if (curr[j] < best) best = curr[j];
    }
    if (best > max) return false;
    prev = curr;
  }
  return prev[b.length] <= max;
}

// Two words are the same word, an initial standing in for it ("J" for
// "JANARDHAN"), or a spelling variant of it.
//
// The spelling tolerance exists because Indian names transliterate
// inconsistently across bank, GST and RERA records — AJIT/AJEET, JAVED/JAVID,
// GOVIND/GOVINDA, SUBRAV/SUBRAO all describe the same person, and 5.5% of live
// CPs differ only this way. It scales with length so short words stay strict:
// RAM and SHYAM are 3 and 5 characters and must never match.
//
// Checked in both directions, because which name counts as "shorter" is decided
// by word count — "AMOL J SALVE" and "AMOL JANARDHAN SALVE" have the same.
function tokenEquivalent(p, q) {
  if (p === q) return true;
  if (p.length === 1 && q.length > 1 && q[0] === p) return true;
  if (q.length === 1 && p.length > 1 && p[0] === q) return true;

  const shortest = Math.min(p.length, q.length);
  if (shortest >= 8) return withinEditDistance(p, q, 2);
  if (shortest >= 4) return withinEditDistance(p, q, 1);
  return false;
}

// Every word of the shorter name appears in the longer one, so a dropped middle
// name still matches: "AMOL JANARDHAN SALVE" vs "AMOL SALVE". A single letter
// matches a word starting with it, covering initials ("AMOL J SALVE").
//
// Comparison is by whole token, never substring — that is what stops
// "RAM" matching "SHYAM". It also requires at least one surviving distinctive
// word, so two names made only of generic words cannot match here.
function tokenSubset(a, b) {
  const x = tokens(a);
  const y = tokens(b);
  if (!x.length || !y.length) return false;

  const shorter = x.length <= y.length ? x : y;
  const longer = x.length <= y.length ? y : x;

  const covered = shorter.every((t) => longer.some((o) => tokenEquivalent(t, o)));
  if (!covered) return false;

  return shorter.some((t) => !GENERIC_WORDS.has(t) && t.length > 1);
}

// The identifying part of a name: everything that is not a legal form or a
// trade descriptor. "SHREE SAI RAM DEVELOPERS LLP" -> ["SHREE","SAI","RAM"].
function distinctiveTokens(name) {
  return tokens(name).filter((t) => !GENERIC_WORDS.has(t));
}

// Compare only the identifying parts. This is what makes "Ram Enterprises" and
// "Shyam Enterprises" different (RAM vs SHYAM) while keeping "Homelocale
// Reality" and "Homelocale Realty" the same (both HOMELOCALE).
//
// If either name is entirely generic there is nothing distinctive to compare,
// so this rule abstains and the whole-name rules above decide.
function distinctiveMatch(a, b) {
  const x = distinctiveTokens(a);
  const y = distinctiveTokens(b);
  if (!x.length || !y.length) return false;

  const joinedX = x.join("");
  const joinedY = y.join("");
  // Joined in original order handles "SHREE GANESH" vs "SHREEGANESH"; sorted
  // handles the same thing when the words are also reordered.
  if (squashedEqual(joinedX, joinedY)) return true;
  if (squashedEqual([...x].sort().join(""), [...y].sort().join(""))) return true;
  if (sameTokenSet(x.join(" "), y.join(" "))) return true;
  return tokenSubset(x.join(" "), y.join(" "));
}

// The single entry point callers should use.
export function namesMatch(a, b) {
  if (!a || !b) return false;
  if (!squash(a) || !squash(b)) return false;

  // Whole-name rules first — cheapest and safest.
  if (squashedEqual(a, b)) return true;
  if (sameTokenSet(a, b)) return true;
  if (tokenSubset(a, b)) return true;

  // Then the identifying part only.
  return distinctiveMatch(a, b);
}

// Exported for tests and for callers wanting one rule in isolation.
export { squash, tokens, squashedEqual, sameTokenSet, tokenSubset, distinctiveTokens, distinctiveMatch, GENERIC_WORDS };
